/**
 * HATDEV-100772 — shared purchase amount / offer resolution and
 * Mixpanel `User_Purchased`-aligned PostHog property builders.
 *
 * Root cause for sub_total:0 / empty offer_id: `??` does not skip `""`, so an
 * empty `offers.price` or `offer_id` masked the real `int_price` / offer id.
 */

import { PosthogEventProperties } from "./posthog.service";

export interface PaymentPurchaseContext {
  purchaseDetail?: any;
  altprosubDetails?: any;
  preferredCurrency?: string;
  paymentMethod?: string;
  brand?: string;
}

export function isPresentScalar(value: unknown): boolean {
  if (value === undefined || value === null) {
    return false;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed !== "" && trimmed !== "null" && trimmed !== "undefined";
  }
  return true;
}

export function firstPresentString(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    if (!isPresentScalar(candidate)) {
      continue;
    }
    return String(candidate).trim();
  }
  return "";
}

/** Reject Firestore paths / refs that accidentally land in name-like fields. */
export function isUsableDisplayLabel(value: unknown): boolean {
  if (!isPresentScalar(value)) {
    return false;
  }
  const text = String(value).trim();
  if (text.startsWith("/")) {
    return false;
  }
  if (text.includes("general_modules/") || text.includes("prosub_id/")) {
    return false;
  }
  if (text.includes("/") && text.includes("details")) {
    return false;
  }
  return true;
}

export function firstPresentDisplayLabel(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    if (!isUsableDisplayLabel(candidate)) {
      continue;
    }
    return String(candidate).trim();
  }
  return "";
}

/**
 * Coerce a single amount candidate. Returns `null` when absent/invalid so
 * callers can try the next candidate (unlike Number("") === 0).
 */
export function coercePurchaseAmountValue(amount: unknown): number | null {
  if (amount === undefined || amount === null) {
    return null;
  }
  if (typeof amount === "string") {
    const trimmed = amount.trim();
    if (trimmed === "" || trimmed === "null" || trimmed === "undefined") {
      return null;
    }
    const cleaned = trimmed.replace(/[^0-9.-]/g, "");
    if (cleaned === "" || cleaned === "-" || cleaned === ".") {
      return null;
    }
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof amount === "object") {
    const map = amount as Record<string, unknown>;
    const mapped =
      map.GBP ??
      map.gbp ??
      map.USD ??
      map.EUR ??
      map.Value ??
      map.value ??
      Object.values(map).find((v) => {
        const n = Number(v);
        return Number.isFinite(n);
      });
    return coercePurchaseAmountValue(mapped);
  }
  const n = Number(amount);
  return Number.isFinite(n) ? n : null;
}

/**
 * Prefer the first positive amount so a leading `0` / free-tier placeholder
 * does not mask a later real `int_price` (e.g. GBP: 1). Falls back to the
 * first finite value (including 0) when nothing positive exists.
 */
export function firstPurchaseAmount(...candidates: unknown[]): number {
  let firstFinite: number | null = null;
  for (const candidate of candidates) {
    const n = coercePurchaseAmountValue(candidate);
    if (n === null) {
      continue;
    }
    if (n > 0) {
      return n;
    }
    if (firstFinite === null) {
      firstFinite = n;
    }
  }
  return firstFinite ?? 0;
}

export function readStoredPurchaseDetail(): any | null {
  try {
    if (typeof localStorage === "undefined") {
      return null;
    }
    const raw = localStorage.getItem("purchaseDetailData");
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export function mergePaymentPurchaseContext(
  purchaseContext?: PaymentPurchaseContext | null,
  fallbacks?: {
    purchaseDetail?: any;
    altprosubDetails?: any;
  }
): PaymentPurchaseContext {
  const stored = readStoredPurchaseDetail();
  return {
    purchaseDetail:
      purchaseContext?.purchaseDetail ??
      fallbacks?.purchaseDetail ??
      stored ??
      undefined,
    altprosubDetails:
      purchaseContext?.altprosubDetails ??
      fallbacks?.altprosubDetails ??
      stored?.offers ??
      stored ??
      undefined,
    preferredCurrency: purchaseContext?.preferredCurrency,
    paymentMethod: purchaseContext?.paymentMethod,
    brand: purchaseContext?.brand,
  };
}

export function resolvePaymentAmountAndOffer(
  amount: unknown,
  offerId: unknown,
  currency: unknown,
  context?: PaymentPurchaseContext | null
): { subTotal: number; offerId: string; currency: string } {
  const purchase = context?.purchaseDetail;
  const alt = context?.altprosubDetails;
  const offers = purchase?.offers ?? alt;
  const currencyKey = firstPresentString(
    currency,
    offers?.currency,
    context?.preferredCurrency,
    "GBP"
  );

  const subTotal = firstPurchaseAmount(
    amount,
    alt?.price,
    purchase?.prize,
    purchase?.amount,
    offers?.price,
    offers?.int_price?.[currencyKey],
    offers?.int_price?.GBP,
    offers?.int_price,
    alt?.int_price?.[currencyKey],
    alt?.int_price?.GBP,
    alt?.int_price
  );

  const resolvedOfferId = firstPresentString(
    offerId,
    alt?.offerid,
    alt?.offer_id,
    purchase?.offer_id,
    offers?.offerid,
    offers?.offer_id,
    purchase?.offers?.offerid,
    purchase?.offers?.offer_id,
    alt?.Offer_ID,
    offers?.Offer_ID
  );

  return {
    subTotal,
    offerId: resolvedOfferId,
    currency: currencyKey || "GBP",
  };
}

function asPosthogScalar(value: unknown): string | number | boolean | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  return String(value);
}

/**
 * Approved bounded `payment_succeeded` schema only (HATDEV-100772).
 * Mixpanel `User_Purchased` is the same business outcome; we do **not** mirror
 * its full commercial property set (no sql_user_id, Jackpot_Value, dual-case ids, etc.).
 */
export function buildPaymentSucceededProps(input: {
  currency: string;
  subTotal: number;
  offerId: string;
  prosubId: string;
  customerStatus: unknown;
  customerCountry: unknown;
  customerPurchaseId: string;
  productType: unknown;
  customerId: string;
  /** Kept for amount/offer resolution callers; not used for extra event props. */
  context?: PaymentPurchaseContext | null;
}): PosthogEventProperties {
  return {
    currency: input.currency,
    sub_total: input.subTotal,
    offer_id: input.offerId,
    prosub_id: input.prosubId,
    customer_status: asPosthogScalar(input.customerStatus),
    customer_country: asPosthogScalar(input.customerCountry),
    customer_purchase_id: input.customerPurchaseId,
    product_type: input.productType != null ? String(input.productType) : undefined,
    c_customer_id: input.customerId,
    source: "gtm_payment_success",
  };
}
